import React, { useEffect, useRef, useState } from "react";
import KuikaButton, {
  KUIKA_BUTTON_ERROR_HOLD_MS,
  KUIKA_BUTTON_ICON_SIZES,
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type KuikaButtonStatus
} from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

type ButtonType = "primary" | "secondary" | "tertiary" | "text";
type ButtonSize = "sm" | "md" | "lg";
type ButtonColor = "default" | "neutral" | "danger";

const SIZES: ButtonSize[] = ["sm", "md", "lg"];
const TYPES: ButtonType[] = ["primary", "secondary", "tertiary", "text"];
const COLORS: ButtonColor[] = ["default", "neutral", "danger"];
const STATUSES: KuikaButtonStatus[] = ["idle", "loading", "success", "error"];

const iconSize: Record<ButtonSize, number> = KUIKA_BUTTON_ICON_SIZES;

// A pretend save, long enough to watch the loading state.
const DEMO_SAVE_MS: number = 1200;

interface IStatusDemoProps {
  size: ButtonSize;
  outcome: "success" | "error";
}

/** A save that runs the whole cycle: idle → loading → success | error → idle. */
const StatusDemo = ({ size, outcome }: IStatusDemoProps): JSX.Element => {
  const [status, setStatus] = useState<KuikaButtonStatus>("idle");
  const timerRef: React.MutableRefObject<number | null> = useRef<number | null>(null);

  useEffect(
    (): (() => void) => (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    },
    []
  );

  const after = (delay: number, next: () => void): void => {
    timerRef.current = window.setTimeout(next, delay);
  };

  const handleSave = (): void => {
    setStatus("loading");
    after(DEMO_SAVE_MS, (): void => {
      setStatus(outcome);
      const hold: number = outcome === "success" ? KUIKA_BUTTON_SUCCESS_HOLD_MS : KUIKA_BUTTON_ERROR_HOLD_MS;
      after(hold, (): void => setStatus("idle"));
    });
  };

  return (
    <KuikaButton type="primary" size={size} status={status} onClick={handleSave}>
      Save changes
    </KuikaButton>
  );
};

/** An aria-disabled button beside a count of how often its onClick ran — it must stay at 0. */
const DeniedDemo = (): JSX.Element => {
  const [clicks, setClicks] = useState<number>(0);

  return (
    <>
      <KuikaButton type="primary" aria-disabled onClick={(): void => setClicks((count: number): number => count + 1)}>
        Publish
      </KuikaButton>
      <KuikaSmallText data-testid="deniedClickCount">onClick ran {clicks} times</KuikaSmallText>
    </>
  );
};

export const ButtonStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Button"
      componentName="KuikaButton"
      sourcePath="ui-kit/kuika-button"
      description="The primary call-to-action. Four types, three sizes, three colors and three lifecycle states compose every variant in this catalogue."
    >
      <VariantGrid
        label="type · size"
        caption="24 / 32 / 40px tall, radius 6 / 8 / 10px — press one: it gives under the pointer, hover only changes colour; Tab for the focus ring"
        rows={TYPES}
        cols={SIZES}
        render={(type: string, size: string): React.ReactNode => (
          <KuikaButton type={type as ButtonType} size={size as ButtonSize} uppercase>
            Button
          </KuikaButton>
        )}
      />
      <VariantGrid
        label="state · size"
        caption="loading keeps its colour and spins; disabled is one opacity over the resting look"
        rows={["default", "loading", "disabled"]}
        cols={SIZES}
        render={(state: string, size: string): React.ReactNode => (
          <KuikaButton
            type="primary"
            size={size as ButtonSize}
            uppercase
            loading={state === "loading"}
            disabled={state === "disabled"}
          >
            Button
          </KuikaButton>
        )}
      />
      <VariantGrid
        label="color · type"
        caption="default · neutral · danger over primary, secondary, tertiary"
        rows={COLORS}
        cols={["primary", "secondary", "tertiary"]}
        render={(color: string, type: string): React.ReactNode => (
          <KuikaButton type={type as ButtonType} color={color as ButtonColor} size="md" uppercase>
            Button
          </KuikaButton>
        )}
      />
      <VariantGrid
        label="icon button · size"
        caption="isIcon is always the 24px rung; its click area is 32px"
        cols={SIZES}
        render={(size: string): React.ReactNode => (
          <KuikaButton type="primary" isIcon size={size as ButtonSize}>
            <KuikaSvg
              name="star"
              width={iconSize[size as ButtonSize]}
              height={iconSize[size as ButtonSize]}
              color="white"
            />
          </KuikaButton>
        )}
      />
      <VariantGrid
        label="status · size"
        caption="status replaces loading for a save: the label rolls, the width follows"
        rows={STATUSES}
        cols={SIZES}
        render={(status: string, size: string): React.ReactNode => (
          <KuikaButton type="primary" size={size as ButtonSize} status={status as KuikaButtonStatus}>
            Save changes
          </KuikaButton>
        )}
      />
      <VariantGrid
        label="status · live"
        caption="press to save — success holds 700ms, error 1600ms, then back to idle"
        rows={["success", "error"]}
        cols={SIZES}
        render={(outcome: string, size: string): React.ReactNode => (
          <StatusDemo size={size as ButtonSize} outcome={outcome as "success" | "error"} />
        )}
      />
      <SpecRow label="aria-disabled" caption="focusable and pressable, but a press shakes instead of running onClick">
        <DeniedDemo />
      </SpecRow>
      <SpecRow label="With trailing icon node" caption="withIconButton slot — small adornment beside the label">
        <KuikaButton
          type="primary"
          withIconButton={<KuikaSvg name="arrow-right" width={14} height={14} color="white" />}
        >
          Continue
        </KuikaButton>
        <KuikaButton
          type="secondary"
          withIconButton={<KuikaSvg name="arrow-right" width={14} height={14} color="currentColor" />}
        >
          Continue
        </KuikaButton>
      </SpecRow>
      <SpecRow label="Uppercase off" caption="default casing is preserved for text and tertiary types">
        <KuikaButton type="primary">Save changes</KuikaButton>
        <KuikaButton type="secondary">Save changes</KuikaButton>
        <KuikaButton type="text">Cancel</KuikaButton>
      </SpecRow>
    </ShowcaseFrame>
  );
};
